import { process } from '@progress/kendo-data-query';
import { GridDataStateChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useOutstanding810LogColumns from 'src/assets/static/edi_admin_tools/useOutstanding810LogColumns';
import GridWrapper from 'src/components/GridWrapper';
import useWindowSize from 'src/hooks/useWindowSize';
import { useContextSelector } from 'use-context-selector';
import { EdiAdminContext } from './EdiAdminContext';
import EdiAdminToolbarActions from './EdiAdminToolbarActions';

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 25, 50, 100],
};

const defaultProps = {
  tableTextId: 'records',
  DataItemKey: 'documentId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable,
  pageable: gridPagerSettings,
} as const;

const Outstanding810LogsView: FC = () => {
  const { t } = useTranslation();
  const columns = useOutstanding810LogColumns();
  const { height } = useWindowSize();

  const outstanding810GridData = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.outstanding810GridData
  );
  const outstanding810Loading = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.outstanding810Loading
  );
  const outstanding810GridDataState = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.outstanding810GridDataState
  );
  const setOutstanding810GridDataState = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.setOutstanding810GridDataState
  );
  const onOutstandingSelectionChange = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.onOutstandingSelectionChange
  );
  const onOutstanding810HeaderSelectionChange = useContextSelector(
    EdiAdminContext,
    (ctx) => ctx.onOutstanding810HeaderSelectionChange
  );

  const gridHeight = useMemo(() => height - 160, [height]);
  const outstanding810ProcessedData = useMemo(
    () => process(outstanding810GridData, outstanding810GridDataState),
    [outstanding810GridData, outstanding810GridDataState]
  );
  const onOutstanding810DataStateChange = useCallback(
    (event: GridDataStateChangeEvent) => {
      setOutstanding810GridDataState(event.dataState);
    },
    [setOutstanding810GridDataState]
  );

  return (
    <main className="px-4">
      <GridWrapper
        id="outstanding810LogsTable"
        className="outstanding-810-logs-view"
        columns={columns}
        dataSource={outstanding810ProcessedData.data}
        total={outstanding810ProcessedData.total}
        dataState={outstanding810GridDataState}
        onDataStateChange={onOutstanding810DataStateChange}
        height={`${gridHeight}px`}
        gridToolbarChildren={<EdiAdminToolbarActions />}
        {...defaultProps}
        loading={outstanding810Loading}
        noRecordText={t('No records to display')}
        tableTextId={t('records')}
        onSelectionChange={onOutstandingSelectionChange}
        onHeaderSelectionChange={onOutstanding810HeaderSelectionChange}
      />
    </main>
  );
};

export default Outstanding810LogsView;
